import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  DEFAULT_MAIL_PREFERENCES,
  cacheMailPreferences,
  normalizeMailPreferences,
  readCachedMailPreferences,
} from '../utils/mailPreferences';
import { useAuth } from './AuthContext';

const MailPreferencesContext = createContext(null);

export const useMailPreferences = () => {
  const ctx = useContext(MailPreferencesContext);
  if (!ctx) {
    throw new Error('useMailPreferences must be used within MailPreferencesProvider');
  }
  return ctx;
};

export const MailPreferencesProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const userKey = String(user?.email || user?.id || '').trim().toLowerCase();
  const [preferences, setPreferences] = useState(() =>
    readCachedMailPreferences(userKey)
  );
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !userKey) {
      setPreferences({ ...DEFAULT_MAIL_PREFERENCES });
      return { ...DEFAULT_MAIL_PREFERENCES };
    }
    setLoading(true);
    try {
      const res = await axios.get('/api/mail-preferences');
      const next = normalizeMailPreferences(res.data?.preferences || {});
      cacheMailPreferences(next, userKey);
      setPreferences(next);
      return next;
    } catch (_) {
      const cached = readCachedMailPreferences(userKey);
      setPreferences(cached);
      return cached;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userKey]);

  const save = useCallback(
    async (nextPrefs) => {
      const normalized = normalizeMailPreferences(nextPrefs);
      const res = await axios.put('/api/mail-preferences', { preferences: normalized });
      const saved = normalizeMailPreferences(res.data?.preferences || normalized);
      cacheMailPreferences(saved, userKey);
      setPreferences(saved);
      return saved;
    },
    [userKey]
  );

  useEffect(() => {
    if (isAuthenticated && userKey) {
      setPreferences(readCachedMailPreferences(userKey));
      refresh();
    } else {
      setPreferences({ ...DEFAULT_MAIL_PREFERENCES });
    }
  }, [isAuthenticated, userKey, refresh]);

  const value = useMemo(
    () => ({
      preferences,
      loading,
      refresh,
      save,
      setPreferencesLocal: (prefs) => {
        const normalized = normalizeMailPreferences(prefs);
        cacheMailPreferences(normalized, userKey);
        setPreferences(normalized);
      },
    }),
    [preferences, loading, refresh, save, userKey]
  );

  return (
    <MailPreferencesContext.Provider value={value}>
      {children}
    </MailPreferencesContext.Provider>
  );
};
